import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { CheckCircle, Coins, ArrowClockwise, Warning, Info, ToggleLeft, ToggleRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import CenterOfferedProfessions from '../components/CenterOfferedProfessions';

const ProPrestazioniPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({}); // service_id -> string price
  const [saving, setSaving] = useState({});
  const [professions, setProfessions] = useState([]);
  const [disabledProfessions, setDisabledProfessions] = useState([]);
  const [me, setMe] = useState({});
  const [filterProf, setFilterProf] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filterProf ? `?profession_code=${encodeURIComponent(filterProf)}` : '';
      const [servicesRes, profRes, meRes] = await Promise.all([
        api.get(`/api/professionals/me/services${q}`),
        api.get('/api/professions'),
        api.get('/api/professionals/me').catch(() => ({ data: {} })),
      ]);
      setItems(servicesRes.data.items || []);
      setProfessions(profRes.data || []);
      setDisabledProfessions(meRes.data?.disabled_professions || []);
      setMe(meRes.data || {});
    } catch (e) {
      toast.error('Errore caricamento listino');
    } finally { setLoading(false); }
  }, [filterProf]);

  useEffect(() => { load(); }, [load]);

  const savePrice = async (svc) => {
    const val = editing[svc.service_id];
    if (val === undefined || val === '') return;
    const num = parseFloat(val);
    if (isNaN(num) || num < 0) { toast.error('Inserisci un prezzo valido'); return; }
    setSaving((s) => ({ ...s, [svc.service_id]: true }));
    try {
      await api.put(`/api/professionals/me/services/${svc.service_id}`, { price_eur: num });
      toast.success(`Prezzo aggiornato: €${num}`);
      setEditing((e) => { const c = { ...e }; delete c[svc.service_id]; return c; });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving((s) => ({ ...s, [svc.service_id]: false }));
    }
  };

  const resetDefault = async (svc) => {
    if (!window.confirm(`Ripristinare il prezzo di default (€${svc.default_price_eur}) per "${svc.name}"?`)) return;
    try {
      await api.delete(`/api/professionals/me/services/${svc.service_id}`);
      toast.success('Prezzo ripristinato al default');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const toggleService = async (svc) => {
    try {
      await api.patch(`/api/professionals/me/services/${svc.service_id}/toggle`, {
        profession_code: '', enabled: !!svc.is_disabled,
      });
      toast.success(svc.is_disabled ? 'Servizio riattivato' : 'Servizio disabilitato');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const toggleCategory = async (code, enabled) => {
    try {
      await api.post('/api/professionals/me/categories/toggle', { profession_code: code, enabled });
      toast.success(enabled ? `Categoria "${code}" abilitata` : `Categoria "${code}" disabilitata`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const someOverridden = items.some((it) => !it.is_using_default);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-12" data-testid="pro-prestazioni-page">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-semibold text-4xl mb-2">Le tue Prestazioni</h1>
            <p className="text-foreground/70">Gestisci le prestazioni che offri e i relativi prezzi.</p>
          </div>
          <Button variant="outline" className="rounded-full" onClick={() => navigate('/professional-dashboard')} data-testid="back-to-dashboard-btn">
            ← Dashboard
          </Button>
        </div>

        {me.is_center && <CenterOfferedProfessions me={me} professions={professions} onSaved={load} />}

        {/* Info banner: default prices notice */}
        {!someOverridden && (
          <Card className="mb-6 p-4 border-l-4 border-l-amber-500 bg-amber-50" data-testid="default-prices-banner">
            <div className="flex items-start gap-3">
              <Warning size={22} className="text-amber-700 mt-0.5" weight="fill" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold mb-1">Consigliamo di modificare i prezzi delle prestazioni</p>
                <p>I prezzi qui sotto sono valori medi nazionali di default. Se non li modifichi, il sistema pubblicherà i tuoi prezzi con i valori qui indicati e accetterai le quote pubblicate. Personalizzali per riflettere la tua tariffa reale.</p>
              </div>
            </div>
          </Card>
        )}

        {/* Category toggles for multi-profession pros / centri */}
        {professions.length > 0 && (
          <Card className="rounded-2xl p-4 mb-6" data-testid="category-toggles">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Info size={16} className="text-primary" />
              <span className="text-sm font-medium">Categorie abilitate</span>
              <span className="text-xs text-foreground/60">— disabilita quelle di cui non vuoi occuparti</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setFilterProf('')}
                className={`px-3 py-1 rounded-full text-xs border ${!filterProf ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border'}`}
                data-testid="cat-filter-all"
              >Tutte</button>
              {professions.map((p) => {
                const isDisabled = disabledProfessions.includes(p.code);
                const active = filterProf === p.code;
                return (
                  <div key={p.code} className="inline-flex items-center rounded-full border border-border overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setFilterProf(p.code)}
                      className={`px-3 py-1 text-xs ${active ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
                      data-testid={`cat-filter-${p.code}`}
                    >
                      {p.label}
                    </button>
                    <button
                      type="button"
                      title={isDisabled ? 'Abilita categoria' : 'Disabilita categoria'}
                      onClick={() => toggleCategory(p.code, isDisabled)}
                      className={`px-2 py-1 text-xs border-l border-border ${isDisabled ? 'bg-red-50 text-red-600' : 'bg-sky-50 text-sky-700'}`}
                      data-testid={`cat-toggle-${p.code}`}
                    >
                      {isDisabled ? <ToggleLeft size={14} /> : <ToggleRight size={14} weight="fill" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-12 text-foreground/60">Caricamento…</div>
        ) : items.length === 0 ? (
          <Card className="p-8 text-center rounded-2xl">
            <p className="text-foreground/70">Nessuna prestazione disponibile per le categorie selezionate.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((svc) => {
              const editVal = editing[svc.service_id] ?? svc.price_eur ?? svc.default_price_eur ?? '';
              const isSaving = saving[svc.service_id];
              return (
                <Card
                  key={svc.service_id}
                  className={`p-4 rounded-2xl ${svc.is_disabled ? 'opacity-60' : ''}`}
                  data-testid={`svc-row-${svc.service_id}`}
                >
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[220px]">
                      <div className="font-medium">{svc.name}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(svc.profession_codes || []).map((pc) => (
                          <Badge key={pc} variant="outline" className="text-[10px]">{pc}</Badge>
                        ))}
                        {!svc.is_using_default && (
                          <Badge className="bg-primary/10 text-primary text-[10px]">Personalizzato</Badge>
                        )}
                        {svc.is_using_default && (
                          <Badge className="bg-amber-100 text-amber-800 text-[10px]">Default nazionale</Badge>
                        )}
                        {svc.category_disabled && (
                          <Badge className="bg-red-100 text-red-800 text-[10px]">Categoria off</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Coins size={16} className="text-primary/70" />
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        value={editVal}
                        onChange={(e) => setEditing((prev) => ({ ...prev, [svc.service_id]: e.target.value }))}
                        className="w-24 h-9 text-right"
                        data-testid={`svc-price-input-${svc.service_id}`}
                      />
                      <span className="text-xs text-foreground/60">€</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        className="rounded-full h-8"
                        disabled={isSaving}
                        onClick={() => savePrice(svc)}
                        data-testid={`svc-save-${svc.service_id}`}
                      >
                        {isSaving ? '…' : <><CheckCircle size={14} className="mr-1" /> Salva</>}
                      </Button>
                      {!svc.is_using_default && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="rounded-full h-8"
                          onClick={() => resetDefault(svc)}
                          data-testid={`svc-reset-${svc.service_id}`}
                          title={`Ripristina default €${svc.default_price_eur}`}
                        >
                          <ArrowClockwise size={14} />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant={svc.is_disabled ? 'default' : 'outline'}
                        className="rounded-full h-8"
                        onClick={() => toggleService(svc)}
                        data-testid={`svc-toggle-${svc.service_id}`}
                      >
                        {svc.is_disabled ? 'Attiva' : 'Disattiva'}
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProPrestazioniPage;
